"use client";

/**
 * MarpDeckEditor — full surface for editing a Marp-generated deck inside the
 * v2 artifact pane (Wave 2, May 2026 — Marp migration; extended Wave 4 with
 * template picker + dual download row).
 *
 * Two tabs:
 *   - **Anteprima**: a Pitch/Keynote `<SlideDeckPreview>` (large stage
 *     slide + clickable thumbnail rail) fed by BE-rendered slide PNGs. The
 *     host injects an authenticated `onRequestPreview(markdown, template)`
 *     fetcher (the editor stays framework-agnostic — no fetch, no apiClient,
 *     and crucially no `<iframe>`, which is dead under Tauri's
 *     `tauri://localhost` origin + JWT-gated preview route). The refetch is
 *     debounced to ~500ms (the markdown-version counter settling into
 *     `previewVersion`) and also fires on a template swap or an external
 *     `previewCacheNonce` bump. The CURRENT (unsaved) markdown buffer is
 *     passed so the preview reflects live edits.
 *   - **Editor**: the shared `MarkdownEditor` (Tiptap). We pipe its
 *     `onSelectionChange` into local state so we can show the
 *     `AiTextPopover` anchored near the highlight.
 *
 * Persistence: NOT owned here — the parent (`ArtifactPane`, Agent D)
 * decides when to flush via PUT /markdown. We emit `onMarkdownChange`
 * (debounced) so the parent can store the dirty draft locally and PUT on
 * confirm.
 *
 * Template picker: top-right of the Tab strip. The picker is purely
 * presentational — it emits the new slug via `onTemplateChange` and the
 * parent owns the persistence + render-URL invalidation. When the parent
 * omits `templateName` / `availableTemplates`, the picker is hidden
 * (back-compat for legacy mounts that don't yet wire template state).
 *
 * Download row: a small two-button strip that appears UNDER the editor
 * surface ONLY when `downloadUrls` is provided AND at least one URL is
 * defined. Mounted independently from the Confirm/Reject CTA pair so the
 * parent can show downloads post-Conferma without having to compose a new
 * action bar.
 *
 * Brand: cream surface, orange accent on the active tab + focus rings.
 * Matches the `DocxEditorInline.tsx` / `PptxViewerInline.tsx` aesthetic
 * (cream shell, hairline borders, `bg-cream/95` chrome strip).
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Download,
  Eye,
  FileText,
  ImageOff,
  Loader2,
  Pencil,
  RotateCw,
  Sparkles,
} from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../primitives/tooltip";
import { AiTextPopover } from "./AiTextPopover";
import { MarkdownEditor } from "./MarkdownEditor";
import {
  MarpImageStylePicker,
  type MarpIllustrationStyle,
} from "./MarpImageStylePicker";
import {
  MarpTemplatePicker,
  type MarpTemplateOption,
} from "./MarpTemplatePicker";
import { SlideDeckPreview } from "./SlideDeckPreview";

/** Re-export so consumers can type their own template lists in one import. */
export type { MarpTemplateOption, MarpTemplateScope } from "./MarpTemplatePicker";
export type { MarpIllustrationStyle } from "./MarpImageStylePicker";

/**
 * Snapshot of the parent-owned compile pipeline. When non-null, the
 * editor disables mutating controls (toggle, picker, Conferma) and
 * surfaces a progress chip in the download row. Stages are intentionally
 * coarse — Wave 6 ships them as parent-simulated milestones (no SSE);
 * real per-image progress reporting is backlog.
 */
export interface MarpCompileBusyState {
  readonly stage: "rendering" | "images" | "compiling";
  /** Optional fine-grained counter for the `images` stage. */
  readonly imagesCount?: {
    readonly done: number;
    readonly total: number;
  };
}

export interface MarpDeckEditorProps {
  /** content_items.id — used for preview URL and AI-rewrite endpoint. */
  readonly contentId: string;
  /** Initial markdown source. The parent fetches this from GET /markdown. */
  readonly initialMarkdown: string;
  /** Emit on every (debounced) edit so the parent can stash a draft. */
  readonly onMarkdownChange?: (next: string) => void;
  /** User clicked Conferma. Parent owns the BE call. */
  readonly onConfirm?: () => void;
  /** User clicked Annulla. */
  readonly onReject?: () => void;
  /** Parent is mid-flight on a BE call — disables CTAs. */
  readonly busy?: boolean;
  /**
   * Currently-selected template slug. When provided alongside
   * `availableTemplates`, the editor mounts the `<MarpTemplatePicker>`
   * in the tab strip. Omit to hide the picker (legacy mode).
   */
  readonly templateName?: string;
  /**
   * Available templates (builtins + customs). Driven by the parent's
   * `useMarpTemplates()` hook; the picker only renders if this list is
   * non-empty.
   */
  readonly availableTemplates?: ReadonlyArray<MarpTemplateOption>;
  /** Fires when the user picks a different template slug. */
  readonly onTemplateChange?: (slug: string) => void;
  /**
   * Optional — fires when the user clicks "Carica tuo template..." in the
   * picker footer. Typically routes to the Settings surface; when omitted
   * the footer item is hidden.
   */
  readonly onOpenTemplateSettings?: () => void;
  /**
   * Render-ready download URLs (post-Confirm). The frozen URL points at
   * the pixel-perfect Marp PPTX; the editable URL points at the
   * Pandoc-rendered fallback. Either side may be omitted — the
   * corresponding button is hidden if its URL is missing. The whole row
   * is hidden when both URLs are absent.
   */
  readonly downloadUrls?: {
    readonly frozen?: string;
    readonly editable?: string;
  };
  /**
   * Wave 6 — opt-in toggle for AI-generated illustrations. When the
   * parent omits both this prop AND the change handler, the toggle is
   * hidden (legacy mounts). When the prop is present, the toggle is
   * rendered in the tab strip immediately after the template picker.
   */
  readonly includeImages?: boolean;
  readonly onIncludeImagesChange?: (next: boolean) => void;
  /**
   * Wave 6 — illustration style. Only meaningful (and only visible)
   * when `includeImages === true`. Defaults to "editorial" at the
   * parent level — the editor itself is presentational and does not
   * apply a default.
   */
  readonly illustrationStyle?: MarpIllustrationStyle;
  readonly onIllustrationStyleChange?: (next: MarpIllustrationStyle) => void;
  /**
   * Wave 6 — parent-owned compile state. When non-null, mutating
   * controls are disabled and a progress chip appears in the download
   * row. The parent is responsible for setting/clearing this — the
   * editor never mutates it.
   */
  readonly compileBusy?: MarpCompileBusyState | null;
  /**
   * Wave 6 — fired when the user clicks the "↻ Rigenera immagini"
   * overlay button on the Preview stage. Visible only when
   * `includeImages === true` AND the deck has been confirmed (i.e.
   * `downloadUrls.frozen` is set). When omitted, the overlay button
   * is hidden.
   */
  readonly onRegenerateImages?: () => void;
  /**
   * Wave 6 — external nonce mixed into the preview refetch trigger.
   * Bump it from the parent (e.g. after a successful image regenerate)
   * to force the preview to re-fetch even when the editor's own
   * markdown-version counter hasn't moved.
   */
  readonly previewCacheNonce?: number;
  /**
   * Tauri-safe graphical preview fetcher (May 2026 — replaces the dead
   * iframe). The host (apps-layer mount) resolves the CURRENT deck into
   * ordered slide PNG `data:` URIs through the authenticated `apiClient`
   * — `@ando/ui` stays framework-agnostic and never touches the network.
   *
   * Called (debounced) whenever the markdown buffer settles, the
   * template changes, or `previewCacheNonce` bumps. Receives the live
   * markdown buffer (reflects unsaved edits → BE `markdown` field) and
   * the current template slug. When omitted, the Anteprima tab shows a
   * "preview unavailable" empty state (legacy mounts that don't wire it).
   */
  readonly onRequestPreview?: (args: {
    readonly markdown: string;
    readonly templateName?: string;
  }) => Promise<ReadonlyArray<string>>;
  /**
   * Render slot for the "Anteprima" tab. When provided it REPLACES the
   * ``SlideDeckPreview`` (live Marp png_thumbs re-render) — the apps layer
   * passes the already-rendered, server-cached PDF viewer
   * (``PptxViewerInline`` over ``GET /documents/{id}/preview``), which is
   * INSTANT and reflects the last COMPILED deck (edits appear after
   * Confirm → recompile). ``@ando/ui`` stays framework-agnostic: it only
   * renders the node, it doesn't know it's a PDF. When omitted, the
   * ``SlideDeckPreview`` + ``onRequestPreview`` path is used as before.
   */
  readonly previewSlot?: React.ReactNode;
  /**
   * Render slot for the "Editor" tab. When provided it REPLACES the raw
   * ``<MarkdownEditor>`` (+ ``AiTextPopover``) — the apps layer passes the
   * structured per-slide ``SlideOutlineEditor`` (``DeckOutlineEditorMount``)
   * so a saved deck edits exactly like one being created, instead of
   * exposing the raw Marp markdown + CSS frontmatter as an unreadable blob.
   * ``@ando/ui`` stays framework-agnostic: it only renders the node. When
   * omitted, the raw markdown editor path stays in place (zero regression
   * for legacy mounts). Mirrors the ``previewSlot`` contract above.
   */
  readonly editorSlot?: React.ReactNode;
  readonly className?: string;
}

type TabKey = "preview" | "editor";

interface Selection {
  readonly text: string;
  readonly from: number;
  readonly to: number;
}

const DEBOUNCE_MS = 500;
const CONTEXT_CHARS = 320; // ~2 short paragraphs worth on each side

function isOnConfirmAvailable(onConfirm: (() => void) | undefined): boolean {
  return typeof onConfirm === "function";
}

function extractContext(markdown: string, from: number, to: number): string {
  const left = markdown.slice(Math.max(0, from - CONTEXT_CHARS), from);
  const right = markdown.slice(to, to + CONTEXT_CHARS);
  return `${left}\n\n${right}`.trim();
}

function hasAnyDownload(
  downloadUrls: MarpDeckEditorProps["downloadUrls"],
): boolean {
  if (!downloadUrls) return false;
  return (
    (typeof downloadUrls.frozen === "string" && downloadUrls.frozen.length > 0) ||
    (typeof downloadUrls.editable === "string" && downloadUrls.editable.length > 0)
  );
}

export function MarpDeckEditor({
  contentId,
  initialMarkdown,
  onMarkdownChange,
  onConfirm,
  onReject,
  busy = false,
  templateName,
  availableTemplates,
  onTemplateChange,
  onOpenTemplateSettings,
  downloadUrls,
  includeImages,
  onIncludeImagesChange,
  illustrationStyle,
  onIllustrationStyleChange,
  compileBusy,
  onRegenerateImages,
  previewCacheNonce = 0,
  onRequestPreview,
  previewSlot,
  editorSlot,
  className,
}: MarpDeckEditorProps): React.JSX.Element {
  const { i18n } = useLingui();
  const [markdown, setMarkdown] = React.useState(initialMarkdown);
  const [activeTab, setActiveTab] = React.useState<TabKey>("preview");
  const [selection, setSelection] = React.useState<Selection | null>(null);
  // Monotonic version bumped on every edit; settles (debounced) into
  // `previewVersion`, which is the trigger for a graphical-preview refetch.
  const [markdownVersion, setMarkdownVersion] = React.useState(0);
  // Debounced version — the preview refetch effect keys off this so we
  // don't re-render slides on every keystroke.
  const [previewVersion, setPreviewVersion] = React.useState(0);
  // ── Graphical preview state (Tauri-safe; replaces the dead iframe) ─────
  const [previewThumbs, setPreviewThumbs] = React.useState<
    ReadonlyArray<string>
  >([]);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  // Stale-guard: a slow response for an outdated buffer can't overwrite a
  // newer one.
  const previewSeqRef = React.useRef(0);
  const [popoverAnchor, setPopoverAnchor] = React.useState<
    { top: number; left: number } | null
  >(null);

  const onMarkdownChangeRef = React.useRef(onMarkdownChange);
  React.useEffect(() => {
    onMarkdownChangeRef.current = onMarkdownChange;
  }, [onMarkdownChange]);

  // Reset internal state when the parent swaps to a different deck.
  React.useEffect(() => {
    setMarkdown(initialMarkdown);
    setMarkdownVersion(0);
    setPreviewVersion(0);
    setPreviewThumbs([]);
    setPreviewError(null);
    setSelection(null);
    setPopoverAnchor(null);
  }, [initialMarkdown, contentId]);

  // Debounce: bump previewVersion + notify parent 500ms after the last edit.
  React.useEffect(() => {
    if (markdownVersion === 0) {
      // Initial mount — preview already in sync, no debounce needed.
      return;
    }
    const handle = window.setTimeout(() => {
      setPreviewVersion(markdownVersion);
      onMarkdownChangeRef.current?.(markdown);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [markdown, markdownVersion]);

  const handleMarkdownChange = React.useCallback((next: string) => {
    setMarkdown(next);
    setMarkdownVersion((v) => v + 1);
  }, []);

  const handleSelectionChange = React.useCallback(
    (text: string, from: number, to: number) => {
      if (!text || text.length === 0) {
        setSelection(null);
        setPopoverAnchor(null);
        return;
      }
      setSelection({ text, from, to });
      // Anchor the popover near the live DOM selection. We use the browser
      // Selection API (Tiptap selection is mirrored into it) so we get
      // pixel coords relative to the viewport, then translate to the
      // editor-shell coord space via the shell's bounding rect.
      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) {
        setPopoverAnchor(null);
        return;
      }
      const rect = domSelection.getRangeAt(0).getBoundingClientRect();
      const shell = shellRef.current;
      if (!shell) {
        setPopoverAnchor(null);
        return;
      }
      const shellRect = shell.getBoundingClientRect();
      setPopoverAnchor({
        // 8px gap below the selection.
        top: rect.bottom - shellRect.top + 8,
        left: Math.max(8, rect.left - shellRect.left),
      });
    },
    [],
  );

  const handleApplyRewrite = React.useCallback(
    (rewritten: string) => {
      if (!selection) return;
      const before = markdown.slice(0, selection.from);
      const after = markdown.slice(selection.to);
      const next = `${before}${rewritten}${after}`;
      setMarkdown(next);
      setMarkdownVersion((v) => v + 1);
      setSelection(null);
      setPopoverAnchor(null);
    },
    [markdown, selection],
  );

  const handleClosePopover = React.useCallback(() => {
    setSelection(null);
    setPopoverAnchor(null);
  }, []);

  // Stable ref for the injected fetcher so the refetch effect keys only
  // off the real triggers (settled markdown, template, cache nonce) and
  // not a fresh function identity on every parent render.
  const onRequestPreviewRef = React.useRef(onRequestPreview);
  React.useEffect(() => {
    onRequestPreviewRef.current = onRequestPreview;
  }, [onRequestPreview]);

  const runPreviewFetch = React.useCallback(async () => {
    const fetcher = onRequestPreviewRef.current;
    if (!fetcher) return;
    previewSeqRef.current += 1;
    const seq = previewSeqRef.current;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const thumbs = await fetcher({ markdown, templateName });
      if (seq !== previewSeqRef.current) return; // superseded
      setPreviewThumbs(thumbs);
    } catch (err) {
      if (seq !== previewSeqRef.current) return;
      const detail =
        err instanceof Error ? err.message : i18n._(msg`Errore di rete`);
      setPreviewError(i18n._(msg`Impossibile generare l'anteprima: ${detail}`));
    } finally {
      if (seq === previewSeqRef.current) setPreviewLoading(false);
    }
  }, [i18n, markdown, templateName]);

  // Keep a stable handle for the effect below (which must NOT re-run on
  // every keystroke — only when the debounced triggers move).
  const runPreviewFetchRef = React.useRef(runPreviewFetch);
  React.useEffect(() => {
    runPreviewFetchRef.current = runPreviewFetch;
  }, [runPreviewFetch]);

  // Single derived trigger key: changes ONLY when a real refetch input
  // settles — the debounced markdown version, a template swap, or an
  // external cache-nonce bump (e.g. post image-regenerate). Collapsing the
  // three into one value the effect actually reads keeps the dep list
  // honest (the fetch itself runs via a stable ref so a fresh `markdown`
  // keystroke doesn't re-trigger it).
  const previewTrigger = `${previewVersion}|${templateName ?? ""}|${previewCacheNonce}`;
  React.useEffect(() => {
    if (activeTab !== "preview") return;
    // `previewTrigger` is read so the dep is genuine, not just a re-run key.
    void previewTrigger;
    void runPreviewFetchRef.current();
  }, [activeTab, previewTrigger]);

  // Cheap slide-count estimate (Marp separates slides with a `---` rule on
  // its own line) so the preview skeleton renders the right number of
  // placeholder thumbnails before the real PNGs land.
  const estimatedSlideCount = React.useMemo(() => {
    const separators = markdown
      .split("\n")
      .filter((line) => /^---\s*$/.test(line.trim())).length;
    return Math.max(separators + 1, 1);
  }, [markdown]);

  const shellRef = React.useRef<HTMLDivElement | null>(null);
  const contextParagraphs = React.useMemo(
    () =>
      selection
        ? extractContext(markdown, selection.from, selection.to)
        : "",
    [markdown, selection],
  );

  const standaloneCtas = isOnConfirmAvailable(onConfirm) || onReject !== undefined;

  // Template picker visibility: the parent must give us BOTH the current
  // slug AND a non-empty list. If the list is loading, we hide the picker
  // rather than render a half-baked dropdown.
  const showTemplatePicker =
    typeof templateName === "string" &&
    Array.isArray(availableTemplates) &&
    availableTemplates.length > 0 &&
    typeof onTemplateChange === "function";

  const showDownloadRow = hasAnyDownload(downloadUrls);

  // ── Wave 6 — AI illustrations controls ────────────────────────────────
  //
  // The toggle is only rendered when the parent explicitly opts in by
  // providing BOTH the current value AND a change handler (matches the
  // template-picker visibility contract above). Legacy mounts that
  // don't yet wire AI-illustration state stay unaffected.
  const showImagesToggle =
    typeof includeImages === "boolean" &&
    typeof onIncludeImagesChange === "function";
  // Style picker piggy-backs on the toggle; it's meaningless when image
  // generation is off, so we hide it entirely rather than disable it.
  const showStylePicker =
    showImagesToggle &&
    includeImages === true &&
    typeof onIllustrationStyleChange === "function";

  // While a compile is in flight ALL mutating controls freeze — the
  // toggle, the style picker, the template picker, and the Conferma CTA.
  // Reject stays clickable so the user can cancel an action that's
  // taking too long (the parent can short-circuit the in-flight promise
  // however it sees fit).
  const compileInFlight = compileBusy !== null && compileBusy !== undefined;
  const controlsDisabled = busy || compileInFlight;

  // Per-image regenerate overlay visibility: only after the deck has
  // been confirmed (frozen URL exists) AND the AI-images toggle is on
  // AND the parent gave us a click handler.
  const showRegenerateOverlay =
    includeImages === true &&
    typeof onRegenerateImages === "function" &&
    typeof downloadUrls?.frozen === "string" &&
    downloadUrls.frozen.length > 0;

  return (
    // Hoist TooltipProvider to component root: shares delay timing across
    // download buttons (and any future tooltipped children), avoids the
    // mount/unmount cost on toggling the download row.
    <TooltipProvider delayDuration={300}>
    <div
      ref={shellRef}
      className={cn(
        "relative flex h-full w-full flex-col bg-cream",
        className,
      )}
    >
      {/* Tab strip — orange-accent active state, hairline divider underneath */}
      <div
        role="tablist"
        aria-label={i18n._(msg`Modalità deck Marp`)}
        className="flex shrink-0 items-center gap-1 border-hairline border-b bg-cream/95 px-3 py-1.5 backdrop-blur"
      >
        <TabButton
          active={activeTab === "preview"}
          onClick={() => setActiveTab("preview")}
          icon={<Eye className="h-3 w-3" aria-hidden strokeWidth={2} />}
          label={i18n._(msg`Anteprima`)}
        />
        <TabButton
          active={activeTab === "editor"}
          onClick={() => setActiveTab("editor")}
          icon={<Pencil className="h-3 w-3" aria-hidden strokeWidth={2} />}
          label={i18n._(msg`Editor`)}
        />

        {/* Right-edge actions: template picker first, AI-images toggle +
            style picker next, Confirm/Reject CTAs at the trailing edge.
            Group all four inside a single `ml-auto` flex so the row
            reads as a single horizontal toolbar without forcing the
            parent to compose an outer wrapper. */}
        <div className="ml-auto flex items-center gap-1.5">
          {showTemplatePicker ? (
            <MarpTemplatePicker
              value={templateName as string}
              templates={availableTemplates as ReadonlyArray<MarpTemplateOption>}
              onChange={(next) => onTemplateChange?.(next)}
              onUpload={onOpenTemplateSettings}
              disabled={controlsDisabled}
            />
          ) : null}

          {showImagesToggle ? (
            <AiImagesToggle
              checked={includeImages === true}
              onChange={(next) =>
                onIncludeImagesChange?.(next)
              }
              disabled={controlsDisabled}
            />
          ) : null}

          {showStylePicker ? (
            <MarpImageStylePicker
              value={illustrationStyle ?? "editorial"}
              onChange={(next) => onIllustrationStyleChange?.(next)}
              disabled={controlsDisabled}
            />
          ) : null}

          {standaloneCtas ? (
            <>
              {onReject ? (
                <button
                  type="button"
                  onClick={onReject}
                  disabled={busy}
                  className="inline-flex h-7 items-center rounded-md px-2.5 font-medium text-[11px] text-ink-2 transition-colors hover:bg-ink-1/[0.04] hover:text-ink-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {i18n._(msg`Annulla`)}
                </button>
              ) : null}
              {onConfirm ? (
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={controlsDisabled}
                  className="inline-flex h-7 items-center rounded-md bg-gradient-to-b from-ando-orange to-ando-orange-hover px-2.5 font-medium text-[11px] text-white shadow-[0_1px_2px_rgba(255,122,26,0.18)] transition-[filter] hover:brightness-[1.04] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
                >
                  {i18n._(msg`Conferma`)}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* Surface */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {activeTab === "preview" ? (
          <div
            role="tabpanel"
            aria-label={i18n._(msg`Anteprima deck`)}
            className="relative h-full w-full"
          >
            {previewSlot !== undefined ? (
              previewSlot
            ) : (
              <SlideDeckPreview
                thumbnails={previewThumbs}
                slideCount={estimatedSlideCount}
                loading={previewLoading}
                error={previewError}
                onRetry={() => void runPreviewFetch()}
              />
            )}
            {showRegenerateOverlay ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onRegenerateImages?.()}
                    disabled={controlsDisabled}
                    aria-label={i18n._(msg`Rigenera immagini`)}
                    className="absolute top-3 right-3 z-10 inline-flex h-7 items-center gap-1.5 rounded-md border border-hairline bg-cream/95 px-2 font-medium text-[11px] text-ando-orange shadow-[0_1px_2px_rgba(0,0,0,0.06)] backdrop-blur transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RotateCw
                      className="h-3 w-3"
                      aria-hidden
                      strokeWidth={2}
                    />
                    {i18n._(msg`Rigenera immagini`)}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {i18n._(msg`Rigenera la prima illustrazione AI del deck`)}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        ) : (
          <div
            role="tabpanel"
            aria-label={i18n._(msg`Editor deck`)}
            className="relative h-full w-full overflow-auto bg-cream-deep/30 p-4"
          >
            {editorSlot !== undefined ? (
              editorSlot
            ) : (
              <>
                <MarkdownEditor
                  value={markdown}
                  onChange={handleMarkdownChange}
                  onSelectionChange={handleSelectionChange}
                  placeholder={i18n._(msg`Scrivi il tuo deck in Marp markdown...`)}
                  className="min-h-full bg-white"
                />
                {selection && popoverAnchor ? (
                  <AiTextPopover
                    contentId={contentId}
                    selectedText={selection.text}
                    contextParagraphs={contextParagraphs}
                    anchor={popoverAnchor}
                    onApplyRewrite={handleApplyRewrite}
                    onClose={handleClosePopover}
                  />
                ) : null}
              </>
            )}
          </div>
        )}
      </div>

      {/* Download row — sits BELOW the surface, when the parent has
          confirmed the deck and exposed render-ready URLs. We split the two
          download intents (frozen vs editable) into a side-by-side pair so
          the user can pick based on downstream use-case (presentation vs.
          last-mile edits in PowerPoint).

          Wave 6: the same row hosts the compile-progress chip when the
          parent has a compile in flight — even when no download URLs
          exist yet (initial compile), so the user sees feedback. */}
      {showDownloadRow || compileInFlight ? (
        <div
          role="group"
          aria-label={i18n._(msg`Download del deck`)}
          className="flex shrink-0 items-center gap-2 border-hairline border-t bg-cream/95 px-3 py-2 motion-reduce:transition-none"
        >
          {compileInFlight && compileBusy ? (
            <CompileProgressChip state={compileBusy} />
          ) : null}
          {downloadUrls?.frozen ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={downloadUrls.frozen}
                  download
                  // Brand orange gradient via shared tokens (ando-orange →
                  // ando-orange-hover) — no hardcoded HEX so a future
                  // rebrand or dark-mode pass only touches the preset.
                  // `motion-reduce` respects `prefers-reduced-motion`.
                  className="hover:-translate-y-px inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-gradient-to-b from-ando-orange to-ando-orange-hover px-3 font-medium text-[12px] text-white shadow-[0_1px_2px_rgba(255,122,26,0.18),0_4px_12px_-4px_rgba(255,122,26,0.32),inset_0_1px_0_rgba(255,255,255,0.18)] transition-[filter,transform] hover:brightness-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream motion-reduce:transform-none motion-reduce:transition-none"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
                  {i18n._(msg`Scarica .pptx (presentazione)`)}
                </a>
              </TooltipTrigger>
              <TooltipContent side="top">
                {i18n._(msg`Pixel-perfect, non editabile in PowerPoint`)}
              </TooltipContent>
            </Tooltip>
          ) : null}
          {downloadUrls?.editable ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={downloadUrls.editable}
                  download
                  className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-hairline bg-white/85 px-3 font-medium text-[12px] text-ink-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:border-black/[0.12] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                >
                  <FileText className="h-3.5 w-3.5 text-ando-orange" aria-hidden strokeWidth={2} />
                  {i18n._(msg`Scarica .pptx (editabile)`)}
                </a>
              </TooltipTrigger>
              <TooltipContent side="top">
                {i18n._(msg`Editabile in PowerPoint, design semplificato`)}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
    </TooltipProvider>
  );
}
MarpDeckEditor.displayName = "MarpDeckEditor";

interface AiImagesToggleProps {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean;
}

/**
 * Brand-styled switch — falls back to `role="switch"` since the UI
 * package does not (yet) ship a Switch primitive. Bundled with a cost
 * hint chip ("~$0.05/deck") on the ON state so the user understands
 * the trade-off without diving into a tooltip.
 */
const AiImagesToggle: React.FC<AiImagesToggleProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  const { i18n } = useLingui();
  return (
  <Tooltip>
    <TooltipTrigger asChild>
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={i18n._(msg`Genera con illustrazioni AI`)}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 font-medium text-[11px] transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            "disabled:cursor-not-allowed disabled:opacity-50",
            checked
              ? "border-ando-orange/30 bg-ando-orange-soft text-ando-orange"
              : "border-hairline bg-white/85 text-ink-3 hover:bg-white hover:text-ink-2",
          )}
        >
          {checked ? (
            <Sparkles className="h-3 w-3" aria-hidden strokeWidth={2} />
          ) : (
            <ImageOff className="h-3 w-3" aria-hidden strokeWidth={2} />
          )}
          <span>{i18n._(msg`Illustrazioni AI`)}</span>
        </button>
        {checked ? (
          <span
            aria-hidden
            className="inline-flex h-5 items-center rounded-full bg-ando-orange-soft px-1.5 font-medium text-[10px] text-ando-orange"
          >
            {i18n._(msg`~$0.05/deck`)}
          </span>
        ) : null}
      </div>
    </TooltipTrigger>
    <TooltipContent side="bottom">
      {i18n._(
        msg`Aggiunge illustrazioni AI brand-coerenti al deck. Aumenta il tempo di compilazione di ~30s e il costo di ~$0.05.`,
      )}
    </TooltipContent>
  </Tooltip>
  );
};
AiImagesToggle.displayName = "MarpDeckEditor.AiImagesToggle";

interface CompileProgressChipProps {
  readonly state: MarpCompileBusyState;
}

/**
 * Small status chip rendered in the download row while a compile is in
 * flight. Three stages map to three labels; the `images` stage also
 * surfaces a "(done/total)" counter when the parent provides one.
 *
 * Uses `role="status"` + `aria-live="polite"` so screen readers
 * announce the transition without preempting the user — Wave 6 spec.
 */
const CompileProgressChip: React.FC<CompileProgressChipProps> = ({ state }) => {
  const { i18n } = useLingui();
  let label: string;
  if (state.stage === "images") {
    const counter = state.imagesCount
      ? ` (${state.imagesCount.done}/${state.imagesCount.total})`
      : "";
    label = i18n._(msg`Generando illustrazioni AI…${counter}`);
  } else if (state.stage === "rendering") {
    label = i18n._(msg`Rendering deck…`);
  } else {
    label = i18n._(msg`Compilando PPTX…`);
  }
  return (
    <div
      role="status"
      aria-live="polite"
      className="inline-flex h-7 items-center gap-1.5 rounded-md border border-ando-orange/30 bg-ando-orange-soft px-2 font-medium text-[11px] text-ando-orange"
    >
      <Loader2
        className="h-3 w-3 animate-spin motion-reduce:animate-none"
        aria-hidden
        strokeWidth={2}
      />
      <span>{label}</span>
    </div>
  );
};
CompileProgressChip.displayName = "MarpDeckEditor.CompileProgressChip";

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: React.ReactNode;
  readonly label: string;
}

const TabButton: React.FC<TabButtonProps> = ({
  active,
  onClick,
  icon,
  label,
}) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 font-medium text-[11.5px] transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
      active
        ? "bg-ando-orange-soft text-ando-orange"
        : "text-ink-2 hover:bg-ink-1/[0.04] hover:text-ink-1",
    )}
  >
    {icon}
    {label}
  </button>
);
